
interface Delta {
    dx_delta: DXDelta | DXMerge;
}

interface DXDelta {
    A: string,
    B: string
}

interface DXMerge {
    edit1: string,
    edit2: string
}

enum DeltaType {
    None,

    ObjWithADelta, // 1a | 
    ObjWithBDelta, // 1b | level 1
    ObjWithABDelta, // 4 |

    ObjDeltaChildSingle, // 2 |
    ObjDeltaChildPair, // 5   | level 2

    DeltaAChild, // 3a   |
    DeltaBChild, // 3b   | level 3
    DeltaABChild, // 6/7 |

    DeltaInAArrayParent,   // 8a
    DeltaInBArrayParent,   // 8b
    DeltaInABArrayParent,  // 8ab

    DeltaInArray, // 9
    DeltaInABArray, // 11

    DeltaInArrayObj, // 10
    DeltaInArrayOb2, // 12



    DeltaStringArray,
    DeltaStringArrayObj,
    DeltaStringArrayObjItem,
}

export enum ChangeType {
    Inserted,
    Removed,
    Modified,
    None
}

interface Position {
    line: number,
    char: number,
    line2: number,
    char2: number
}

export interface ViewChange {
    changeType: ChangeType,
    isEmphasized: boolean,
    line: number,
    rawLine: number,
    number: number,
    endLine?: number
}

export interface SelectorChange {
    changeType: ChangeType,
    line: number,
    rawLine: number
}

export interface SelectorInfo {
    changeType: ChangeType,
    rawLine: number,
    change: number
}

export interface ViewChangeWord extends ViewChange {
    startChar: number,
    endChar: number
}

export enum DiffReportOperation {
    Other,
    ThreeWayMerge
}

interface DeltaStringArray {
    dx_delta_string: StringArrayItems;
}

type StringArrayItems = [string | Delta][];

export interface DiffReportData {
    textContent: string,
    viewChangeLines: ViewChange[],
    viewChangeWords: ViewChangeWord[],
    selectorChangeLines: SelectorChange[]
}

export class DiffReport {
    public static EOL = '\n';

    public static getDiffReportData(sourceObject: any, indentSize: number, operation: DiffReportOperation): DiffReportData {
        const indent = ' '.repeat(indentSize);
        const diffParts: string[] = [];
        const viewChangeLines: ViewChange[] = [];
        const viewChangeWords: ViewChangeWord[] = [];
        const selectorChangeLines: SelectorChange[] = [];

        DiffReport.createDiffFromDelta(indent, ChangeType.None, viewChangeLines, viewChangeWords, selectorChangeLines, sourceObject, diffParts, operation);

        return (
            {
                textContent: diffParts.join(''),
                viewChangeLines: viewChangeLines,
                viewChangeWords: viewChangeWords,
                selectorChangeLines: selectorChangeLines
            }
        );
    }

    private static createDiffFromDelta(
        indent: string,
        objChangeType: ChangeType,
        viewChangeLines: ViewChange[],
        viewChangeWords: ViewChangeWord[],
        selectorChangeLines: SelectorChange[],
        obj: any,
        diffParts: string[],
        operation: DiffReportOperation,
        key: string = '',
        pos: Position = { line: 1, char: 1, line2: 1, char2: 1 },
        parentType: DeltaType = DeltaType.None,
        inheritedIsArrayItem = false,
        isLastChild = true,
        nestLevel = 1,
        isEmphasized = true,
    ) {

        if (obj instanceof Object) {
            // an object with properties - not a property-value or an array value
            const isArray = obj instanceof Array;
            const entries = Object.entries(obj);
            const entriesLength = entries.length;
            const entriesLast = entriesLength - 1;
            let deltaStringArrayItems: StringArrayItems = [];
            let valForObjA: any = null;
            let valForObjB: any = null;

            for (let i = 0; i < entriesLength; i++) {
                let [k, child] = entries[i];
                let currentType: DeltaType = DeltaType.None;
                switch (parentType) {
                    case DeltaType.None:
                        if (typeof child === 'object' && child !== null) {
                            if (i === 0 && nestLevel === 1 && k === 'dx_delta') {
                                // root object property is dx_delta therefore the inputs do not align
                                const deltaParent = child as any;
                                const aObject = operation === DiffReportOperation.ThreeWayMerge ? deltaParent.edit1 : deltaParent.A;
                                const bObject = operation === DiffReportOperation.ThreeWayMerge ? deltaParent.edit2 : deltaParent.B;

                                const aObjectString = JSON.stringify(aObject, null, indent);
                                const lineCountA = DiffReport.countLines(aObjectString);
                                diffParts.push(aObjectString);
                                viewChangeLines.push({ line: pos.line, endLine: pos.line + lineCountA - 1, rawLine: pos.line2, changeType: ChangeType.Removed, isEmphasized: false, number: 1 });
                                diffParts.push(this.EOL);

                                const startLineB = pos.line + lineCountA;
                                const bOBjectString = JSON.stringify(bObject, null, indent);
                                const lineCountB = DiffReport.countLines(bOBjectString);
                                diffParts.push(bOBjectString);
                                viewChangeLines.push({ line: startLineB, endLine: startLineB + lineCountB - 1, rawLine: pos.line2, changeType: ChangeType.Inserted, isEmphasized: false, number: 1 });
                                return;
                            } else if (child.hasOwnProperty('dx_delta')) {
                                const deltaParent = child as any;
                                const deltaObj = deltaParent.dx_delta;

                                if ((operation === DiffReportOperation.ThreeWayMerge && deltaObj.edit1 !== undefined) || (operation === DiffReportOperation.Other && deltaObj.A !== undefined)) {
                                    valForObjA = operation === DiffReportOperation.ThreeWayMerge ? deltaObj.edit1 : deltaObj.A;
                                    if ((operation === DiffReportOperation.ThreeWayMerge && deltaObj.edit2 !== undefined) || (operation === DiffReportOperation.Other && deltaObj.B !== undefined)) {
                                        valForObjB = operation === DiffReportOperation.ThreeWayMerge ? deltaObj.edit2 : deltaObj.B;
                                        currentType = isArray ? DeltaType.DeltaInABArrayParent : DeltaType.ObjWithABDelta;
                                        selectorChangeLines.push({ changeType: ChangeType.Modified, line: pos.line + 1, rawLine: pos.line2 + 1 });
                                    } else {
                                        currentType = isArray ? DeltaType.DeltaInAArrayParent : DeltaType.ObjWithADelta;
                                        selectorChangeLines.push({ changeType: ChangeType.Removed, line: pos.line + 1, rawLine: pos.line2 + 1 });
                                    }
                                } else {
                                    currentType = isArray ? DeltaType.DeltaInBArrayParent : DeltaType.ObjWithBDelta;
                                    selectorChangeLines.push({ changeType: ChangeType.Inserted, line: pos.line + 1, rawLine: pos.line2 + 1 });
                                }
                            } else if (child.hasOwnProperty('dx_delta_string')) {
                                selectorChangeLines.push({ changeType: ChangeType.Modified, line: pos.line + 1, rawLine: pos.line2 + 1 });
                                currentType = DeltaType.DeltaStringArray;
                                let stringArrayParent = child as DeltaStringArray;
                                deltaStringArrayItems = stringArrayParent.dx_delta_string;
                            }
                        }
                        break;
                    case DeltaType.DeltaInAArrayParent:
                    case DeltaType.DeltaInBArrayParent:
                        currentType = DeltaType.DeltaInArray;
                        break;
                    case DeltaType.DeltaInABArrayParent:
                        currentType = DeltaType.DeltaInABArray;
                        break;
                    case DeltaType.ObjDeltaChildSingle:
                        currentType = k === 'A' ? DeltaType.DeltaAChild : DeltaType.DeltaBChild;
                        break;
                    case DeltaType.DeltaAChild:
                        objChangeType = ChangeType.Removed;
                        break;
                    case DeltaType.DeltaBChild:
                        objChangeType = ChangeType.Inserted;
                        break;
                    case DeltaType.ObjDeltaChildPair:
                        if (typeof child === 'object') {
                            currentType = k === 'A' ? DeltaType.DeltaAChild : DeltaType.DeltaBChild;
                            objChangeType = ChangeType.Modified;
                        } else {
                            currentType = DeltaType.DeltaABChild;
                        }

                        break;
                    case DeltaType.ObjWithADelta:
                    case DeltaType.ObjWithBDelta:
                        currentType = DeltaType.ObjDeltaChildSingle;
                        break;
                    case DeltaType.ObjWithABDelta:
                        currentType = DeltaType.ObjDeltaChildPair;
                        break;
                    case DeltaType.DeltaInArray:
                        currentType = DeltaType.DeltaInArrayObj;
                        objChangeType = k === 'A' ? ChangeType.Removed : ChangeType.Inserted;
                        break;
                    case DeltaType.DeltaInABArray:
                        currentType = k === 'A' ? DeltaType.DeltaInArrayObj : DeltaType.DeltaInArrayOb2;
                        if (child !== null && typeof child === 'object') {
                            objChangeType = ChangeType.Modified;
                        }
                        // TODOX: set objChangeType?
                        break;
                }

                const rawIsLastItem = i === entriesLast;

                const isRealObject = (
                    parentType === DeltaType.None ||
                    parentType === DeltaType.DeltaAChild ||
                    parentType === DeltaType.DeltaBChild ||
                    parentType === DeltaType.DeltaInArrayObj ||
                    parentType === DeltaType.DeltaInArrayOb2);

                let isObjectShown = isRealObject;
                if (!isObjectShown) {
                    // object must also be shown if its the B item in a dx_delta object that's inside an array - so we get the ] char
                    isObjectShown = (parentType === DeltaType.DeltaInABArray && currentType === DeltaType.DeltaInArrayOb2);
                }
                let isLastItem = isRealObject ? rawIsLastItem : isLastChild;

                let newIsLastItem = isLastItem;
                if (parentType === DeltaType.DeltaInABArray && currentType === DeltaType.DeltaInArrayObj) {
                    newIsLastItem = false;
                };
                if (parentType === DeltaType.DeltaInArrayOb2 && objChangeType === ChangeType.Modified && key === 'B') {
                    // do nothing
                } else if (i === 0 && isObjectShown) {
                    if (isArray) {
                        let char = '';
                        if (parentType === DeltaType.DeltaInArrayObj) {
                            char = indent.repeat(nestLevel - 3) + '[';
                        } else {
                            char = inheritedIsArrayItem ? indent.repeat(nestLevel - 1) + '[' : '[';
                        }
                        diffParts.push(char);
                    } else {
                        let char = '';
                        if (parentType === DeltaType.DeltaInArrayObj) {
                            if (inheritedIsArrayItem) {
                                pos.line++;
                                char = this.EOL;
                                this.pushChangeLines(parentType, objChangeType, viewChangeLines, pos, selectorChangeLines.length, isEmphasized);
                            }
                            char += indent.repeat(nestLevel - 3) + '{';
                        } else if (inheritedIsArrayItem) {
                            if (objChangeType === ChangeType.None) {
                                char = indent.repeat(nestLevel - 1) + '{';
                            } else {
                                char = indent.repeat(nestLevel - 3) + '{';
                            }
                        } else {
                            char = '{';
                        }
                        diffParts.push(char);
                    }
                }
                if (isArray) {
                    pos.line2++;
                    if (currentType === DeltaType.DeltaStringArray) {
                        const propIndent = indent.repeat(nestLevel);
                        const propLine = this.EOL + propIndent;
                        const changeNumber = selectorChangeLines.length;

                        pos.line++;
                        diffParts.push(propLine);
                        viewChangeLines.push({ line: pos.line, rawLine: pos.line2, changeType: ChangeType.Removed, isEmphasized: false, number: changeNumber });
                        pos.line2++;
                        const aParts: string[] = [], bParts: string[] = [];
                        this.processDeltaStringArray(propLine.length + 1, deltaStringArrayItems, pos, aParts, bParts, viewChangeWords, operation);
                        diffParts.push(JSON.stringify(aParts.join('')) + ',');
                        pos.line++;
                        diffParts.push(propLine);

                        const comma = rawIsLastItem ? '' : ',';
                        diffParts.push(JSON.stringify(bParts.join('')) + comma);
                        viewChangeLines.push({ line: pos.line, rawLine: pos.line2, changeType: ChangeType.Inserted, isEmphasized: false, number: changeNumber });
                    } else if (currentType === DeltaType.DeltaInABArrayParent) {
                        if (valForObjA !== undefined) {
                            const changeNumber = selectorChangeLines.length;
                            pos.line++;
                            diffParts.push(this.EOL + indent.repeat(nestLevel));
                            // handle A version of object:
                            viewChangeLines.push({ line: pos.line, rawLine: pos.line2, changeType: ChangeType.Removed, isEmphasized: false, number: changeNumber });
                            this.createDiffFromDelta(indent, ChangeType.Modified, viewChangeLines, viewChangeWords, selectorChangeLines, valForObjA, diffParts, operation, k, pos, DeltaType.DeltaAChild, false, false, nestLevel + 3, false);

                            // handle B version of object
                            pos.line++;
                            viewChangeLines.push({ line: pos.line, rawLine: pos.line2, changeType: ChangeType.Inserted, isEmphasized: false, number: changeNumber });
                            diffParts.push(this.EOL + indent.repeat(nestLevel));
                            this.createDiffFromDelta(indent, ChangeType.Modified, viewChangeLines, viewChangeWords, selectorChangeLines, valForObjB, diffParts, operation, k, pos, DeltaType.DeltaBChild, false, rawIsLastItem, nestLevel + 3, false);
                        }
                    } else if (parentType === DeltaType.None || parentType === DeltaType.DeltaAChild || parentType === DeltaType.DeltaBChild) {
                        pos.line++;
                        let ct = objChangeType;
                        if (currentType === DeltaType.DeltaInBArrayParent || currentType === DeltaType.DeltaInAArrayParent) {
                            ct = currentType === DeltaType.DeltaInAArrayParent ? ChangeType.Removed : ChangeType.Inserted;
                        }
                        // TODOX:
                        // if (currentType === DeltaType.DeltaInBArrayParent || currentType === DeltaType.DeltaInAArrayParent ||  currentType === DeltaType.DeltaInABArrayParent) {
                        //     ct = (currentType === DeltaType.DeltaInBArrayParent || currentType === DeltaType.DeltaInABArrayParent) ? ChangeType.Inserted : ChangeType.Removed;
                        // }
                        this.pushChangeLines(parentType, ct, viewChangeLines, pos, selectorChangeLines.length, isEmphasized);
                        diffParts.push(this.EOL);
                    }
                } else {
                    pos.line2++;
                    switch (parentType) {
                        case DeltaType.None:
                        case DeltaType.DeltaAChild:
                        case DeltaType.DeltaBChild:
                        case DeltaType.DeltaInArrayObj:
                        case DeltaType.DeltaInArrayOb2:
                            let correctedLevel = parentType === DeltaType.None ? nestLevel : nestLevel - 2;
                            if (parentType === DeltaType.None && objChangeType !== ChangeType.None) {
                                correctedLevel -= 2;
                            }

                            const propIndent = indent.repeat(correctedLevel);

                            if ((objChangeType === ChangeType.None) && (currentType === DeltaType.DeltaInArray || currentType === DeltaType.DeltaInABArray || currentType === DeltaType.ObjDeltaChildSingle)) {
                                // then do not show property name
                            } else {
                                const changeNumber = selectorChangeLines.length;
                                pos.line++;
                                const propName = JSON.stringify(k) + ": ";
                                const propLine = this.EOL + propIndent + propName;
                                diffParts.push(propLine);

                                if (currentType === DeltaType.ObjWithADelta || currentType === DeltaType.DeltaStringArray || parentType === DeltaType.DeltaAChild || objChangeType === ChangeType.Removed) {
                                    const emph = currentType !== DeltaType.DeltaStringArray && isEmphasized;
                                    viewChangeLines.push({ line: pos.line, rawLine: pos.line2, changeType: ChangeType.Removed, isEmphasized: emph, number: changeNumber });
                                } else if (currentType === DeltaType.ObjWithBDelta || parentType === DeltaType.DeltaBChild || objChangeType === ChangeType.Inserted) {
                                    viewChangeLines.push({ line: pos.line, rawLine: pos.line2, changeType: ChangeType.Inserted, isEmphasized: isEmphasized, number: selectorChangeLines.length });
                                }
                                if (objChangeType === ChangeType.Modified) {
                                    if (parentType === DeltaType.DeltaInArrayObj) {
                                        viewChangeLines.push({ line: pos.line, rawLine: pos.line2, changeType: ChangeType.Removed, isEmphasized: false, number: changeNumber });
                                    } else if (parentType === DeltaType.DeltaInArrayOb2) {
                                        viewChangeLines.push({ line: pos.line, rawLine: pos.line2, changeType: ChangeType.Inserted, isEmphasized: false, number: changeNumber });
                                    }
                                }
                                if (currentType === DeltaType.DeltaStringArray) {
                                    pos.line2++;
                                    const aParts: string[] = [], bParts: string[] = [];
                                    this.processDeltaStringArray(propLine.length + 1, deltaStringArrayItems, pos, aParts, bParts, viewChangeWords, operation);
                                    diffParts.push(JSON.stringify(aParts.join('')) + ',');
                                    pos.line++;
                                    diffParts.push(propLine);

                                    const comma = rawIsLastItem ? '' : ',';
                                    diffParts.push(JSON.stringify(bParts.join('')) + comma);
                                    viewChangeLines.push({ line: pos.line, rawLine: pos.line2, changeType: ChangeType.Inserted, isEmphasized: false, number: changeNumber });
                                } else if (currentType === DeltaType.ObjWithABDelta) {
                                    if (valForObjA !== undefined) {
                                        // handle A version of object:
                                        viewChangeLines.push({ line: pos.line, rawLine: pos.line2, changeType: ChangeType.Removed, isEmphasized: false, number: changeNumber });
                                        this.createDiffFromDelta(indent, ChangeType.Modified, viewChangeLines, viewChangeWords, selectorChangeLines, valForObjA, diffParts, operation, k, pos, DeltaType.DeltaAChild, false, false, nestLevel + 3, false);

                                        // handle B version of object
                                        viewChangeLines.push({ line: pos.line + 1, rawLine: pos.line2, changeType: ChangeType.Inserted, isEmphasized: false, number: changeNumber });
                                        pos.line++;
                                        diffParts.push(propLine);
                                        this.createDiffFromDelta(indent, ChangeType.Modified, viewChangeLines, viewChangeWords, selectorChangeLines, valForObjB, diffParts, operation, k, pos, DeltaType.DeltaBChild, false, rawIsLastItem, nestLevel + 3, false);
                                    }
                                }
                            }
                            break;
                    }
                }

                if (currentType !== DeltaType.DeltaStringArray && currentType !== DeltaType.ObjWithABDelta && currentType !== DeltaType.DeltaInABArrayParent) {
                    const isDeltaPart = parentType === DeltaType.ObjDeltaChildSingle ||
                        parentType === DeltaType.ObjDeltaChildPair ||
                        parentType === DeltaType.DeltaInAArrayParent ||
                        parentType === DeltaType.DeltaInBArrayParent ||
                        parentType === DeltaType.DeltaInABArrayParent
                    const newIsArray = currentType === DeltaType.None || isDeltaPart ? isArray : inheritedIsArrayItem;
                    const fixedCurrentType = parentType === DeltaType.DeltaInArrayObj && isArray ? parentType : currentType;
                    this.createDiffFromDelta(indent, objChangeType, viewChangeLines, viewChangeWords, selectorChangeLines, child, diffParts, operation, k, pos, fixedCurrentType, newIsArray, newIsLastItem, nestLevel + 1, isEmphasized);
                }
                if (rawIsLastItem) {
                    pos.line2++;
                }
                if (parentType !== DeltaType.DeltaInABArray && (isLastItem || (rawIsLastItem && objChangeType !== ChangeType.None)) && isObjectShown) {
                    pos.line++;
                    if (objChangeType !== ChangeType.Modified && objChangeType !== ChangeType.None) {
                        this.pushChangeLines(parentType, objChangeType, viewChangeLines, pos, selectorChangeLines.length, isEmphasized);
                    }
                    const isChanged = parentType === DeltaType.DeltaAChild || parentType === DeltaType.DeltaBChild || objChangeType !== ChangeType.None;
                    let fixedLevel = isChanged ? nestLevel - 3 : nestLevel - 1;
                    const comma = isLastChild ? '' : ',';

                    let closeChar = isArray || currentType === DeltaType.DeltaInArrayOb2 || currentType === DeltaType.DeltaInArrayObj ? ']' : '}';
                    if (!isChanged && (currentType === DeltaType.DeltaInArrayOb2 || currentType === DeltaType.DeltaInArrayObj)) {
                        fixedLevel -= 2;
                    }
                    if (fixedLevel < 0) {
                        console.log('Error: fixedLevel less than zero:', fixedLevel);
                    }
                    if (parentType === DeltaType.DeltaInArrayObj && objChangeType === ChangeType.Modified && newIsLastItem) {
                        pos.line--;
                        if (key === 'A') {
                            diffParts.push(',');
                        }
                    } else {
                        diffParts.push(this.EOL + indent.repeat(fixedLevel) + closeChar + comma);
                    }
                }
            }
            if (entriesLength === 0) {
                const comma = isLastChild ? '' : ',';
                const text = isArray ? '[]' : '{}';
                diffParts.push(text + comma);
            }
        } else {
            // this is an object-property value or an array value
            const skipItem = parentType === DeltaType.DeltaABChild && key === 'A';
            if (!skipItem) {

                let valueString = '';

                if (inheritedIsArrayItem && parentType === DeltaType.None && objChangeType === ChangeType.None) {
                    valueString = indent.repeat(nestLevel - 1) + JSON.stringify(obj);
                } else if (inheritedIsArrayItem && parentType !== DeltaType.DeltaInArrayObj && (parentType === DeltaType.DeltaAChild || parentType === DeltaType.DeltaBChild || objChangeType !== ChangeType.None)) {
                    valueString = indent.repeat(nestLevel - 3) + JSON.stringify(obj);
                } else if (parentType === DeltaType.DeltaInArrayObj || parentType === DeltaType.DeltaInArrayOb2) {
                    if (parentType === DeltaType.DeltaInArrayOb2 || (parentType === DeltaType.DeltaInArrayObj && inheritedIsArrayItem)) {
                        pos.line++;
                        valueString = this.EOL;
                    }
                    if (objChangeType !== ChangeType.None && parentType === DeltaType.DeltaInArrayObj && inheritedIsArrayItem) {
                        // TODO: check isEmphasized value for modification
                        viewChangeLines.push({ line: pos.line, rawLine: pos.line2, changeType: objChangeType, isEmphasized: true, number: selectorChangeLines.length });
                    } else if (objChangeType === ChangeType.None) {
                        let parentChange = ChangeType.None;
                        if (key === 'A') {
                            parentChange = ChangeType.Removed;
                        } else if (key === 'B') {
                            parentChange = ChangeType.Inserted;
                        } else {
                            parentChange = ChangeType.None;
                        }
                        viewChangeLines.push({ line: pos.line, rawLine: pos.line2, changeType: parentChange, isEmphasized: false, number: selectorChangeLines.length });
                    }

                    valueString = valueString + indent.repeat(nestLevel - 3) + JSON.stringify(obj);
                } else {
                    valueString = JSON.stringify(obj);
                }
                const comma = isLastChild ? '' : ',';
                diffParts.push(valueString + comma); //   + info + (DeltaType[parentType] + ' L' + pos.line + ' L' + pos.line2)
            }
        }

    };

    private static countLines(aObjectString: string) {
        let lineCount = 0;
        let prevIndex = 0;
        do {
            prevIndex = aObjectString.indexOf('\n', prevIndex + 1);
            lineCount++;
        } while (prevIndex !== -1);
        return lineCount;
    }

    static processDeltaStringArray(charOffset: number, deltaStringArray: StringArrayItems, pos: Position, aParts: string[], bParts: string[], viewChangeWords: ViewChangeWord[], operation: DiffReportOperation) {
        let charStartA = charOffset;
        let charStartB = charOffset;

        deltaStringArray.forEach((item) => {
            const obj = item as unknown;

            const linePosB = pos.line + 1;

            if (item instanceof Object && item.hasOwnProperty('dx_delta')) {
                pos.line2 += 2;
                const deltaParent = obj as any;
                const delta = deltaParent.dx_delta;
                if ((operation === DiffReportOperation.ThreeWayMerge && delta.edit1) || (operation === DiffReportOperation.Other && delta.A)) {
                    pos.line2++;
                    const aDelta = operation === DiffReportOperation.ThreeWayMerge ? delta.edit1 : delta.A;
                    ; const strLen = JSON.stringify(aDelta).length - 2;
                    viewChangeWords.push({ line: pos.line, rawLine: pos.line2, changeType: ChangeType.Removed, startChar: charStartA, endChar: charStartA + strLen, isEmphasized: false, number: -1 });
                    charStartA += strLen;
                    aParts.push(aDelta);
                }
                if ((operation === DiffReportOperation.ThreeWayMerge && delta.edit2) || (operation === DiffReportOperation.Other && delta.B)) {
                    const bDelta = operation === DiffReportOperation.ThreeWayMerge ? delta.edit2 : delta.B;
                    const strLen = JSON.stringify(bDelta).length - 2;
                    viewChangeWords.push({ line: linePosB, rawLine: pos.line2, changeType: ChangeType.Inserted, startChar: charStartB, endChar: charStartB + strLen, isEmphasized: false, number: -1 });
                    charStartB += strLen;
                    pos.line2++;
                    bParts.push(bDelta);
                }
                pos.line2 += 2;
            } else {
                pos.line2++;
                const strLen = JSON.stringify(obj).length - 2;
                charStartA += strLen;
                charStartB += strLen;
                aParts.push(obj as string);
                bParts.push(obj as string);
            }
        });
        pos.line2 += 2;
    }

    static pushChangeLines(parentType: DeltaType, objChangeType: ChangeType, viewChangeLines: ViewChange[], pos: Position, number: number, isEmphasized: boolean) {
        let ct = objChangeType;
        if (parentType === DeltaType.DeltaAChild) {
            ct = ChangeType.Removed;
        } else if (parentType === DeltaType.DeltaBChild) {
            ct = ChangeType.Inserted;
        }
        if (ct !== ChangeType.None) {
            viewChangeLines.push({ line: pos.line, rawLine: pos.line2, changeType: objChangeType, isEmphasized: isEmphasized, number: number });
        }
    }
}